import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import MultiRfidSearchComponent from '../components/MultiRfidSearchComponent';
import { getCoral, getCoralByRfid } from '../services/api';
import { CoralResponse } from '../types';
import { accent } from '../theme';

type LookupMode = 'rfid' | 'coralId';

const SelectFragmentParent: React.FC = () => {
  const navigate = useNavigate();
  const [lookupMode, setLookupMode] = useState<LookupMode>('rfid');
  const [coralIdInput, setCoralIdInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const continueWithParent = useCallback(
    (coral: CoralResponse | null) => {
      if (!coral) {
        setError('Parent coral not found');
        return;
      }
      if (!coral.rfid) {
        setError('Only a coral with an active RFID can be used as a parent');
        return;
      }
      navigate(`/coral/view/${encodeURIComponent(coral.coralId)}/fragments/create`);
    },
    [navigate]
  );

  const handleRfidSearch = useCallback(
    async (rfids: string[]) => {
      if (rfids.length !== 1) {
        setError('Scan one parent RFID at a time');
        return;
      }
      try {
        setSearching(true);
        setError(null);
        continueWithParent(await getCoralByRfid(rfids[0]));
      } catch (lookupError: any) {
        setError(lookupError?.message || 'Parent coral not found');
      } finally {
        setSearching(false);
      }
    },
    [continueWithParent]
  );

  const handleCoralIdSearch = useCallback(async () => {
    const coralId = coralIdInput.trim();
    if (!coralId) {
      return;
    }
    try {
      setSearching(true);
      setError(null);
      continueWithParent(await getCoral(coralId));
    } catch (lookupError: any) {
      setError(lookupError?.message || 'Parent coral not found');
    } finally {
      setSearching(false);
    }
  }, [continueWithParent, coralIdInput]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Create Fragments
          </Typography>
          <Typography color="text.secondary">
            Find the parent coral first. You will review its details before entering the new
            fragment information.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 3, ...accent.coral }}>
          <Stack spacing={2}>
            <Typography variant="h6">Find Parent Coral</Typography>
            <ToggleButtonGroup
              value={lookupMode}
              exclusive
              onChange={(_, value: LookupMode | null) => value && setLookupMode(value)}
              size="small"
              aria-label="parent coral lookup mode"
            >
              <ToggleButton value="rfid">RFID Scan</ToggleButton>
              <ToggleButton value="coralId">Coral ID</ToggleButton>
            </ToggleButtonGroup>

            {lookupMode === 'rfid' ? (
              <MultiRfidSearchComponent onSearch={handleRfidSearch} isSearching={searching} />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Internal Coral ID"
                  placeholder="Enter CoralID"
                  value={coralIdInput}
                  onChange={(event) => setCoralIdInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleCoralIdSearch();
                    }
                  }}
                  disabled={searching}
                  fullWidth
                  size="small"
                />
                <Button
                  variant="contained"
                  onClick={() => void handleCoralIdSearch()}
                  disabled={searching || !coralIdInput.trim()}
                  startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
                >
                  Continue
                </Button>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
};

export default SelectFragmentParent;
